import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import { Trash2, Save } from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { purchaseReturnActions } from '../../action/purchaseReturnActions';

const EditPurchaseReturn = ({ returnId, isViewMode = false, onSuccess, onClose }) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [originalStatus, setOriginalStatus] = useState('');
  const [stockAdjusted, setStockAdjusted] = useState(false);
  const [previouslyPaid, setPreviouslyPaid] = useState(0);

  // Utility function to round money values to 2 decimal places
  const roundMoney = (value) => {
    if (value === null || value === undefined) return 0;
    // Round to 2 decimal places and fix floating point errors
    const rounded = Math.round((parseFloat(value) + Number.EPSILON) * 100) / 100;
    // Handle cases like 0.0000001 that should be 0
    return Math.abs(rounded) < 0.01 ? 0 : rounded;
  };

  // Format currency with proper rounding
  const formatCurrency = (amount) => {
    const rounded = roundMoney(amount);
    return `₹${rounded.toFixed(2)}`;
  };

  // Determine payment status based on total paid amount
  const determinePaymentStatus = (totalPaid, grandTotal) => {
    const roundedPaid = roundMoney(totalPaid);
    const roundedTotal = roundMoney(grandTotal);

    if (roundedPaid >= roundedTotal) return 'paid';
    if (roundedPaid > 0) return 'partial';
    return 'unpaid';
  };

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending', description: 'Return pending approval', adjustsStock: false },
    { value: 'approved', label: 'Approved', description: 'Return approved', adjustsStock: false },
    { value: 'received', label: 'Received', description: 'Items received - Stock REDUCED', adjustsStock: true },
    { value: 'rejected', label: 'Rejected', description: 'Return rejected', adjustsStock: false },
    { value: 'completed', label: 'Completed', description: 'Return completed - Stock REDUCED', adjustsStock: true },
    { value: 'cancelled', label: 'Cancelled', description: 'Return cancelled', adjustsStock: false }
  ];

  // Reason options
  const reasonOptions = [
    { value: 'Damaged', label: 'Damaged Goods' },
    { value: 'Defective', label: 'Defective Product' },
    { value: 'Wrong Item', label: 'Wrong Item Received' },
    { value: 'Excess Supply', label: 'Excess Supply' },
    { value: 'Quality Issue', label: 'Quality Issue' },
    { value: 'Expired', label: 'Expired Product' },
    { value: 'Other', label: 'Other' }
  ];

  // Clean up modal backdrops
  const cleanupModalBackdrops = () => {
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach(backdrop => backdrop.remove());
    document.body.classList.remove('modal-open');
    document.body.style.paddingRight = '';
    document.body.style.overflow = '';
  };

  // Show Bootstrap modal
  const showBootstrapModal = () => {
    cleanupModalBackdrops();
    const modalElement = document.getElementById('edit-purchase-return');
    if (modalElement) {
      if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
        window.$(modalElement).modal('show');
      } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
        let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
        if (!modalInstance) {
          modalInstance = new window.bootstrap.Modal(modalElement);
        }
        modalInstance.show();
      } else {
        modalElement.classList.add('show');
        modalElement.style.display = 'block';
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
        document.body.classList.add('modal-open');
      }
    }
  };

  // Hide Bootstrap modal
  const hideBootstrapModal = () => {
    const modalElement = document.getElementById('edit-purchase-return');
    if (modalElement) {
      if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
        window.$(modalElement).modal('hide');
        setTimeout(cleanupModalBackdrops, 300);
      } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
        const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.hide();
          setTimeout(cleanupModalBackdrops, 300);
        }
      } else {
        modalElement.classList.remove('show');
        modalElement.style.display = 'none';
        cleanupModalBackdrops();
      }
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    hideBootstrapModal();
    if (onClose) onClose();
    if (onSuccess) onSuccess();
  };

  // Load return data
  const loadReturnData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await purchaseReturnActions.getReturn(returnId);

      if (result.success && result.data) {
        const returnData = result.data;
        console.log('Return data loaded:', returnData);

        // Round all monetary values
        const subtotal = roundMoney(returnData.subtotal || 0);
        const tax = roundMoney(returnData.tax || 0);
        const discount = roundMoney(returnData.discount || 0);
        const shipping = roundMoney(returnData.shipping || 0);
        const grand_total = roundMoney(returnData.grand_total || (subtotal - discount + tax + shipping));

        // Store previously paid amount from database
        const previouslyPaidAmount = roundMoney(returnData.paid_amount || 0);
        setPreviouslyPaid(previouslyPaidAmount);

        // Current payment starts at 0 in edit mode
        const currentPayment = 0;
        const totalPaid = previouslyPaidAmount + currentPayment;
        const due_amount = roundMoney(grand_total - totalPaid);
        const payment_status = determinePaymentStatus(totalPaid, grand_total);

        // Store original status for comparison
        setOriginalStatus(returnData.status);
        setStockAdjusted(returnData.status === 'received' || returnData.status === 'completed');

        // Format items with proper structure
        const formattedItems = (returnData.items || []).map(item => {
          const itemQty = roundMoney(item.return_quantity || item.quantity || 0);
          const itemPrice = roundMoney(item.unit_price || 0);
          const itemDiscount = roundMoney(item.discount || 0);
          const itemTaxPercent = roundMoney(item.tax_percent || 0);
          const itemTaxAmount = roundMoney(item.tax_amount || 0);
          const itemTotal = roundMoney(item.total_cost || 0);

          return {
            id: item.id,
            purchase_item_id: item.purchase_item_id,
            product_id: item.product_id,
            product_name: item.product_name,
            sku: item.sku || 'N/A',
            original_quantity: roundMoney(item.original_quantity || itemQty),
            return_quantity: itemQty,
            unit_price: itemPrice,
            discount: itemDiscount,
            tax_percent: itemTaxPercent,
            tax_amount: itemTaxAmount,
            total_cost: itemTotal,
            reason: item.reason || ''
          };
        });

        setFormData({
          id: returnData.id,
          purchase_id: returnData.purchase_id,
          reference_no: returnData.reference_no,
          return_date: returnData.return_date || returnData.created_at,
          supplier_id: returnData.supplier_id,
          supplier_name: returnData.supplier_name,
          original_reference_no: returnData.original_reference_no,
          subtotal,
          tax,
          discount,
          shipping,
          grand_total,
          previously_paid: previouslyPaidAmount,
          paid_amount: currentPayment, // Current payment (starts at 0)
          due_amount,
          payment_status,
          reason: returnData.reason || '',
          notes: returnData.notes || '',
          status: returnData.status || 'pending',
          items: formattedItems,
          created_by: returnData.created_by
        });

        setSelectedDate(dayjs(returnData.return_date || returnData.created_at));

        // Set supplier info
        if (returnData.supplier_name) {
          setSelectedSupplier({
            value: returnData.supplier_id,
            label: returnData.supplier_name
          });
        }
      } else {
        Swal.fire('Error', result.message || 'Failed to load return data', 'error');
        handleModalClose();
      }
    } catch (error) {
      console.error('Error loading return data:', error);
      Swal.fire('Error', 'Failed to load return data', 'error');
      handleModalClose();
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  useEffect(() => {
    if (returnId) {
      loadReturnData();
      setTimeout(() => showBootstrapModal(), 50);
    }

    return () => {
      cleanupModalBackdrops();
    };
  }, [returnId, loadReturnData]);

  const handleCancel = () => {
    handleModalClose();
  };

  // Determine if stock adjustment is needed
  const needsStockAdjustment = (newStatus, oldStatus) => {
    const stockAdjustingStatuses = ['received', 'completed'];
    const wasAdjusted = stockAdjustingStatuses.includes(oldStatus);
    const willAdjust = stockAdjustingStatuses.includes(newStatus);

    return {
      shouldAdjust: !wasAdjusted && willAdjust,
      shouldReverse: wasAdjusted && !willAdjust
    };
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData || isViewMode) return;

    try {
      setSaving(true);

      // Validate items
      if (!formData.items || formData.items.length === 0) {
        Swal.fire('Error', 'At least one return item is required', 'error');
        return;
      }

      // Calculate totals
      const grandTotal = roundMoney(formData.grand_total);
      const currentPayment = roundMoney(formData.paid_amount);
      const totalPaid = roundMoney(previouslyPaid + currentPayment);
      const dueAmount = roundMoney(grandTotal - totalPaid);
      const paymentStatus = determinePaymentStatus(totalPaid, grandTotal);

      console.log('Submitting with:', {
        grandTotal,
        previouslyPaid,
        currentPayment,
        totalPaid,
        dueAmount,
        paymentStatus
      });

      // Check stock adjustment needs
      const { shouldAdjust, shouldReverse } = needsStockAdjustment(formData.status, originalStatus);

      // Confirm stock adjustment if needed
      if (shouldAdjust || shouldReverse) {
        const action = shouldAdjust ? 'reduce stock' : 'reverse stock adjustment';
        const confirm = await Swal.fire({
          title: 'Stock Adjustment',
          text: `Changing status from "${originalStatus}" to "${formData.status}" will ${action}. Continue?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, proceed'
        });

        if (!confirm.isConfirmed) {
          setSaving(false);
          return;
        }
      }

      // Prepare update data
      const updateData = {
        id: formData.id,
        return_date: selectedDate.format('YYYY-MM-DD'),
        status: formData.status,
        payment_status: paymentStatus,
        paid_amount: totalPaid, // Save total paid amount
        due_amount: dueAmount,
        grand_total: grandTotal,
        shipping: roundMoney(formData.shipping),
        tax: roundMoney(formData.tax),
        discount: roundMoney(formData.discount),
        subtotal: roundMoney(formData.subtotal),
        reason: formData.reason,
        notes: formData.notes,
        items: formData.items.map(item => ({
          id: item.id,
          purchase_item_id: item.purchase_item_id,
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku,
          quantity: roundMoney(item.return_quantity),
          unit_price: roundMoney(item.unit_price),
          discount: roundMoney(item.discount),
          tax_percent: roundMoney(item.tax_percent),
          tax_amount: roundMoney(item.tax_amount),
          total_cost: roundMoney(item.total_cost),
          reason: item.reason
        }))
      };

      const result = await purchaseReturnActions.updateReturn(formData.id, updateData);

      if (result.success) {
        // Handle stock adjustment if needed
        if (shouldAdjust) {
          await purchaseReturnActions.adjustStock(formData.id);
        } else if (shouldReverse) {
          await purchaseReturnActions.reverseStockAdjustment(formData.id);
        }

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Purchase return updated successfully',
          timer: 3000
        });

        handleModalClose();
      } else {
        Swal.fire('Error', result.message || 'Failed to update purchase return', 'error');
      }
    } catch (error) {
      console.error('Error updating purchase return:', error);
      Swal.fire('Error', 'Failed to update purchase return', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (isViewMode) return;

    const result = await Swal.fire({
      title: 'Delete Return?',
      text: 'Are you sure you want to delete this return? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const deleteResult = await purchaseReturnActions.deleteReturn(formData.id);

        if (deleteResult.success) {
          Swal.fire('Deleted!', 'Return has been deleted.', 'success');
          handleModalClose();
        } else {
          Swal.fire('Error', deleteResult.message || 'Failed to delete return', 'error');
        }
      } catch (error) {
        console.error('Error deleting return:', error);
        Swal.fire('Error', 'Failed to delete return', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  // Handle quantity change
  const handleQuantityChange = (index, value) => {
    if (isViewMode) return;

    const qty = roundMoney(value);

    setFormData(prev => {
      const updatedItems = [...prev.items];
      const item = updatedItems[index];

      const maxQty = item.original_quantity;
      const actualQty = Math.max(0, Math.min(qty, maxQty));

      // Calculate tax and total for this item
      const taxAmount = roundMoney((item.unit_price * actualQty - item.discount) * (item.tax_percent / 100));
      const totalCost = roundMoney((item.unit_price * actualQty - item.discount) + taxAmount);

      updatedItems[index] = {
        ...item,
        return_quantity: actualQty,
        tax_amount: taxAmount,
        total_cost: totalCost
      };

      // Recalculate all totals
      const subtotal = roundMoney(updatedItems.reduce((sum, i) => sum + (i.unit_price * i.return_quantity), 0));
      const discount = roundMoney(updatedItems.reduce((sum, i) => sum + i.discount, 0));
      const tax = roundMoney(updatedItems.reduce((sum, i) => sum + i.tax_amount, 0));
      const shipping = roundMoney(prev.shipping);
      const grandTotal = roundMoney(subtotal - discount + tax + shipping);

      // Calculate new due amount based on previously paid + current payment
      const currentPayment = roundMoney(prev.paid_amount);
      const totalPaid = roundMoney(previouslyPaid + currentPayment);
      const dueAmount = roundMoney(grandTotal - totalPaid);
      const paymentStatus = determinePaymentStatus(totalPaid, grandTotal);

      return {
        ...prev,
        items: updatedItems,
        subtotal,
        discount,
        tax,
        grand_total: grandTotal,
        due_amount: dueAmount,
        payment_status: paymentStatus
      };
    });
  };

  // Handle paid amount change (current payment) - FIXED VALIDATION
  const handlePaidAmountChange = (value) => {
    if (isViewMode) return;

    // Allow any positive number, validation will be done on submit
    const currentPayment = parseFloat(value) || 0;

    setFormData(prev => {
      const grandTotal = roundMoney(prev.grand_total);
      // Don't validate here, just update
      const totalPaid = roundMoney(previouslyPaid + currentPayment);
      const dueAmount = roundMoney(grandTotal - totalPaid);
      const paymentStatus = determinePaymentStatus(totalPaid, grandTotal);

      return {
        ...prev,
        paid_amount: currentPayment,
        due_amount: dueAmount,
        payment_status: paymentStatus
      };
    });
  };

  // Handle shipping change
  const handleShippingChange = (value) => {
    if (isViewMode) return;

    const shipping = roundMoney(value);
    setFormData(prev => {
      const grandTotal = roundMoney(prev.subtotal - prev.discount + prev.tax + shipping);
      const currentPayment = roundMoney(prev.paid_amount);
      const totalPaid = roundMoney(previouslyPaid + currentPayment);
      const dueAmount = roundMoney(grandTotal - totalPaid);
      const paymentStatus = determinePaymentStatus(totalPaid, grandTotal);

      return {
        ...prev,
        shipping,
        grand_total: grandTotal,
        due_amount: dueAmount,
        payment_status: paymentStatus
      };
    });
  };

  // Handle reason change
  const handleReasonChange = (index, option) => {
    if (isViewMode) return;

    const reason = option?.value || '';
    setFormData(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = { ...updatedItems[index], reason };
      return { ...prev, items: updatedItems };
    });
  };

  // Handle remove item
  const handleRemoveItem = (index) => {
    if (isViewMode) return;

    Swal.fire({
      title: 'Remove Item?',
      text: 'Are you sure you want to remove this item from the return?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => {
          const updatedItems = prev.items.filter((_, i) => i !== index);

          const subtotal = roundMoney(updatedItems.reduce((sum, i) => sum + (i.unit_price * i.return_quantity), 0));
          const discount = roundMoney(updatedItems.reduce((sum, i) => sum + i.discount, 0));
          const tax = roundMoney(updatedItems.reduce((sum, i) => sum + i.tax_amount, 0));
          const shipping = roundMoney(prev.shipping);
          const grandTotal = roundMoney(subtotal - discount + tax + shipping);

          const currentPayment = roundMoney(prev.paid_amount);
          const totalPaid = roundMoney(previouslyPaid + currentPayment);
          const dueAmount = roundMoney(grandTotal - totalPaid);
          const paymentStatus = determinePaymentStatus(totalPaid, grandTotal);

          return {
            ...prev,
            items: updatedItems,
            subtotal,
            discount,
            tax,
            grand_total: grandTotal,
            due_amount: dueAmount,
            payment_status: paymentStatus
          };
        });

        Swal.fire('Removed!', 'Item has been removed.', 'success');
      }
    });
  };

  // Handle status change
  const handleStatusChange = (option) => {
    if (isViewMode) return;

    const newStatus = option?.value || 'pending';
    setFormData(prev => ({ ...prev, status: newStatus }));
  };

  if (loading) {
    return (
      <div className="modal fade" id="edit-purchase-return" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isViewMode ? 'View' : 'Edit'} Purchase Return</h5>
              <button type="button" className="btn-close" onClick={handleCancel} aria-label="Close"></button>
            </div>
            <div className="modal-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading purchase return data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate display values
  const displayGrandTotal = roundMoney(formData?.grand_total || 0);
  const displayCurrentPayment = roundMoney(formData?.paid_amount || 0);
  const displayTotalPaid = roundMoney(previouslyPaid + displayCurrentPayment);
  const displayDueAmount = roundMoney(displayGrandTotal - displayTotalPaid);
  const displayPaymentStatus = determinePaymentStatus(displayTotalPaid, displayGrandTotal);

  // Calculate maximum allowed payment (don't use displayDueAmount for max)
  //const maxPayment = displayDueAmount + displayCurrentPayment; // This allows full payment

  return (
    <div className="modal fade" id="edit-purchase-return" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isViewMode ? 'View' : 'Edit'} Purchase Return
              {formData && <small className="ms-2 text-muted">{formData.reference_no}</small>}
            </h5>
            <button type="button" className="btn-close" onClick={handleCancel} aria-label="Close"></button>
          </div>

          <div className="modal-body">
            {formData ? (
              <form onSubmit={handleSubmit}>
                {/* Basic Information - Read Only */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Reference No.</label>
                    <input type="text" className="form-control bg-light" value={formData.reference_no} readOnly />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Supplier</label>
                    <input type="text" className="form-control bg-light" value={selectedSupplier?.label || formData.supplier_name} readOnly />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Original Purchase</label>
                    <input type="text" className="form-control bg-light" value={formData.original_reference_no} readOnly />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Return Date</label>
                    <DatePicker
                      value={selectedDate}
                      onChange={(date) => !isViewMode && setSelectedDate(date)}
                      format="DD-MM-YYYY"
                      className="form-control"
                      style={{ width: '100%' }}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                {/* Status and Payment */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Status</label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(opt => opt.value === formData.status)}
                      onChange={handleStatusChange}
                      isDisabled={isViewMode}
                      getOptionLabel={(option) => (
                        <div>
                          <span>{option.label}</span>
                          <small className="text-muted d-block">{option.description}</small>
                        </div>
                      )}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Main Reason</label>
                    <Select
                      options={reasonOptions}
                      value={reasonOptions.find(opt => opt.value === formData.reason)}
                      onChange={(option) => !isViewMode && setFormData(prev => ({ ...prev, reason: option?.value || '' }))}
                      isDisabled={isViewMode}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Payment Status</label>
                    <input
                      type="text"
                      className={`form-control fw-bold ${displayPaymentStatus === 'paid' ? 'bg-success text-white' :
                          displayPaymentStatus === 'partial' ? 'bg-warning' : 'bg-danger text-white'
                        }`}
                      value={displayPaymentStatus.toUpperCase()}
                      readOnly
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Stock Status</label>
                    <input
                      type="text"
                      className={`form-control ${stockAdjusted ? 'bg-success text-white' : 'bg-light'}`}
                      value={stockAdjusted ? 'Stock Adjusted' : 'No Stock Adjustment'}
                      readOnly
                    />
                  </div>
                </div>

                {/* Payment Summary Card */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card bg-light">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Previously Paid</small>
                            <h6 className="fw-bold text-primary">{formatCurrency(previouslyPaid)}</h6>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Current Payment</small>
                            <h6 className="fw-bold text-success">{formatCurrency(displayCurrentPayment)}</h6>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Total Paid</small>
                            <h6 className="fw-bold text-success">{formatCurrency(displayTotalPaid)}</h6>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Remaining Due</small>
                            <h6 className={`fw-bold ${displayDueAmount > 0 ? 'text-danger' : 'text-success'}`}>
                              {formatCurrency(displayDueAmount)}
                            </h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                {formData.items && formData.items.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Return Items</h6>
                    <div className="table-responsive border rounded">
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Product</th>
                            <th>SKU</th>
                            <th className="text-end">Original Qty</th>
                            <th className="text-end">Return Qty</th>
                            <th className="text-end">Unit Price</th>
                            <th className="text-end">Total</th>
                            <th>Reason</th>
                            {!isViewMode && <th className="text-center">Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={item.id || index}>
                              <td>{index + 1}</td>
                              <td>{item.product_name}</td>
                              <td>{item.sku}</td>
                              <td className="text-end">{roundMoney(item.original_quantity)}</td>
                              <td className="text-end" style={{ width: '100px' }}>
                                {isViewMode ? (
                                  roundMoney(item.return_quantity)
                                ) : (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={item.return_quantity}
                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                    min="0"
                                    max={item.original_quantity}

                                  />
                                )}
                              </td>
                              <td className="text-end">{formatCurrency(item.unit_price)}</td>
                              <td className="text-end fw-bold">{formatCurrency(item.total_cost)}</td>
                              <td>
                                {isViewMode ? (
                                  item.reason
                                ) : (
                                  <Select
                                    options={reasonOptions}
                                    value={reasonOptions.find(opt => opt.value === item.reason)}
                                    onChange={(option) => handleReasonChange(index, option)}
                                    className="select-sm"
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: '30px',
                                        fontSize: '13px',
                                        width: '120px'
                                      })
                                    }}
                                  />
                                )}
                              </td>
                              {!isViewMode && (
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleRemoveItem(index)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label">Subtotal</label>
                    <input type="text" className="form-control bg-light" value={formatCurrency(formData.subtotal)} readOnly />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Tax</label>
                    <input type="text" className="form-control bg-light" value={formatCurrency(formData.tax)} readOnly />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Discount</label>
                    <input type="text" className="form-control bg-light" value={formatCurrency(formData.discount)} readOnly />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Shipping</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.shipping}
                      onChange={(e) => handleShippingChange(e.target.value)}
                      disabled={isViewMode}
                      min="0"

                    />
                  </div>
                </div>

                {/* Payment Section - FIXED VALIDATION */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Grand Total</label>
                    <input type="text" className="form-control bg-light fw-bold text-primary" value={formatCurrency(displayGrandTotal)} readOnly />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Current Payment</label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        className="form-control"
                        value={displayCurrentPayment}
                        onChange={(e) => handlePaidAmountChange(e.target.value)}
                        disabled={isViewMode}
                        min="0"
                        max={displayGrandTotal} // Allow up to grand total

                        placeholder="Enter payment amount"
                      />
                    </div>
                    <small className="text-muted">Enter the amount you want to pay now</small>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Balance Due</label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="text"
                        className={`form-control bg-light ${displayDueAmount > 0 ? 'text-danger' : 'text-success'}`}
                        value={formatCurrency(displayDueAmount)}
                        readOnly
                      />
                    </div>
                    <small className="text-muted">Remaining balance after this payment</small>
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={formData.notes || ''}
                    onChange={(e) => !isViewMode && setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    readOnly={isViewMode}
                    placeholder="Any additional notes..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="d-flex justify-content-between">
                  <div>
                    {!isViewMode && (
                      <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={saving}>
                        {saving ? 'Deleting...' : 'Delete Return'}
                      </button>
                    )}
                  </div>
                  <div>
                    <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                      Cancel
                    </button>
                    {!isViewMode && (
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={16} className="me-2" />
                            Update Return
                          </>
                        )}
                      </button>
                    )}
                    {isViewMode && (
                      <button type="button" className="btn btn-primary" onClick={handleCancel}>
                        Close
                      </button>
                    )}
                  </div>
                </div>
              </form>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted">No return data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EditPurchaseReturn.propTypes = {
  returnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isViewMode: PropTypes.bool,
  onSuccess: PropTypes.func,
  onClose: PropTypes.func
};

export default EditPurchaseReturn;